import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import QTIAssessmentItem from './QTIAssessmentItem';

import assessmentActions from '../../actions/AssessmentActions';
import visitedQuestionsActions from '../../actions/VisitedQuestionsActions';

import durationTrackingService from '../../services/DurationTrackingService';
import LoadTracker from '../../services/LoadTracker';
import responseService from '../../services/ResponseService';
import { t } from '../../services/i18nService';
import questionHelperServices from '../../services/questionView/QuestionHelperServices';
import scrollingService from '../../services/ScrollingService';
import stimulusHighlightsService from '../../services/StimulusHighlightsService';

import assessmentReader from '../../store/readHelpers/AssessmentReader';
import storeManager from '../../store/StoreManager';

import ScantronSketches from './ScantronSketches';
import QuestionDisplayFooter from './QuestionDisplayFooter';
import QuestionDisplayFlag from './itemFlag/QuestionDisplayFlag';
import QuestionNumber from './QuestionNumber';
import QuestionManualSelection from './QuestionManualSelection';
import QuestionPDFPanelDisplay from './QuestionPDFPanelDisplay';
import QuestionRubricBlockDisplay from './QuestionRubricBlockDisplay';
import QuestionKeyWordListDisplay from './QuestionKeyWordList';
import FeedbackDisplay from './feedback/FeedbackDisplay';
import ModalFeedbackDisplay from './feedback/ModalFeedbackDisplay';

import styles from './QuestionDisplay.scss';
import constants from '../../qti/Constants';
import SectionRubricBlock from '../../themes/components/fjord/SectionRubricBlock';
import questionDisplaySelector from '../../selectors/question/QuestionDisplaySelector';
import QuestionRubricBlockWithTitle from './QuestionRubricBlockWithTitle';
import navigationService from '../../services/NavigationService';
import reloginService from '../../services/ReloginService';
import stateCacheService from '../../services/StateCache';

export class Question extends React.PureComponent {
    static propTypes = {
        assessmentRunId: PropTypes.number,
        activateAndScrollToQuestion: PropTypes.func,
        additionalQuestionClassList: PropTypes.array.isRequired,
        additionalWrapperClassList: PropTypes.array.isRequired,
        annotatableQuestionHandler: PropTypes.func,
        choiceSequences: PropTypes.object.isRequired,
        context: PropTypes.string.isRequired,
        correctResponses: PropTypes.object,
        currentScorableItem: PropTypes.number,
        extendedRenderingActivated: PropTypes.bool.isRequired,
        feedback: PropTypes.object,
        flaggedItems: PropTypes.array,
        hasHighlight: PropTypes.bool,
        headerId: PropTypes.string.isRequired,
        highlightContentItemId: PropTypes.number,
        interactionSupportsRTL: PropTypes.bool.isRequired,
        isContentBlock: PropTypes.bool,
        isCurrent: PropTypes.bool,
        isDocument: PropTypes.bool,
        isForm: PropTypes.bool,
        isGapMatchQuestion: PropTypes.bool.isRequired,
        isInSectionView: PropTypes.bool.isRequired,
        isLastQuestionInCurrentNestedSection: PropTypes.bool,
        isManualSelectorReadOnly: PropTypes.bool.isRequired,
        isReadOnly: PropTypes.bool,
        isReadOnlyBasedOnManualSelectionSetup: PropTypes.bool.isRequired,
        isRemoteOralExam: PropTypes.bool.isRequired,
        isSplitQuestionView: PropTypes.bool.isRequired,
        isViewMediaScreen: PropTypes.bool.isRequired,
        isVisited: PropTypes.bool.isRequired,
        isWordFormationQuestion: PropTypes.bool.isRequired,
        mainQuestionWrapperLabel: PropTypes.string,
        mode: PropTypes.string,
        onReady: PropTypes.func.isRequired,
        outcomeVariables: PropTypes.object,
        presignedUrlsForPDFPanel: PropTypes.object,
        question: PropTypes.object.isRequired,
        questionInteractionVisibility: PropTypes.object,
        questionNumberId: PropTypes.string.isRequired,
        questionSelection: PropTypes.object,
        section: PropTypes.object,
        sectionHasStimulus: PropTypes.bool,
        sectionId: PropTypes.number,
        sectionQuestionsCount: PropTypes.number,
        settingValues: PropTypes.object,
        shouldScrollToFirstInteraction: PropTypes.bool.isRequired,
        shouldShowInteractionFeedback: PropTypes.bool.isRequired,
        shouldShowManualSelector: PropTypes.bool.isRequired,
        shouldShowModalFeedback: PropTypes.bool.isRequired,
        shouldShowPdfPanel: PropTypes.bool.isRequired,
        shouldShowScantronSketches: PropTypes.bool.isRequired,
        shouldShowTitle: PropTypes.bool.isRequired,
        showFlagging: PropTypes.bool.isRequired,
        showKeyWordListBlock: PropTypes.bool.isRequired,
        showQuestionRubricBlock: PropTypes.bool.isRequired,
        stimulusPosition: PropTypes.string,
        themeInstance: PropTypes.object.isRequired,
        themeQuestionLabel: PropTypes.object,
        ui: PropTypes.object.isRequired,
        useNumberPerInteraction: PropTypes.bool.isRequired,
        userResponses: PropTypes.object.isRequired,
        viewMedia: PropTypes.string,
    };

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillMount() {
        this.initLoadTracker(this.props);
    }

    componentDidMount() {
        const { assessmentRunId, question, isInSectionView, hasHighlight, mode, highlightContentItemId, isVisited,
            shouldScrollToFirstInteraction, themeInstance, isGapMatchQuestion, currentScorableItem } = this.props;
        if (isGapMatchQuestion) {
            navigationService.scrollToGapInput(question.contentRevisionId, currentScorableItem).catch(console.log);
        }

        if (assessmentRunId && question?.contentRevisionId && reloginService.shouldStoreNextQuestion()) {
            stateCacheService.getStateCacheInstance().setLastVisitedQuestion(assessmentRunId, question.contentRevisionId);
        }

        if (!isVisited) {
            this.setAsVisited(question.contentRevisionId);
        }
        this.renderedTracker(true);

        if (shouldScrollToFirstInteraction) {
            scrollingService.scrollToFirstInteraction();
        }

        if (!isInSectionView) {
            scrollingService.scrollToTop();
            this.focus();
            stimulusHighlightsService.goToHighlight(question, hasHighlight, mode, highlightContentItemId);
        }

        durationTrackingService.setNewDurationReferenceTimestamp(question.contentRevisionId);
        themeInstance.performPostComponentMountActions(`#question-${question.contentRevisionId}`);
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillUpdate(nextProps) {
        if (nextProps.question !== this.props.question) {
            this.initLoadTracker(nextProps);
            responseService.updateItemResultOnNavigation(this.props.question.contentRevisionId);
            durationTrackingService.setNewDurationReferenceTimestamp(nextProps.question.contentRevisionId);
        }
    }

    componentDidUpdate(prevProps) {
        const {
            isInSectionView,
            question,
            hasHighlight,
            mode,
            highlightContentItemId,
            isVisited,
            isWordFormationQuestion,
            themeInstance,
        } = this.props;

        if (isWordFormationQuestion && this.props.currentScorableItem !== prevProps.currentScorableItem) {
            this.scrollToScorableInput();
        }

        if (prevProps.question === question) {
            return;
        }

        this.renderedTracker(true);
        if (!isVisited) {
            this.setAsVisited(question.contentRevisionId);
        }

        if (!isInSectionView) {
            scrollingService.scrollToTop();
            this.focus();
            stimulusHighlightsService.goToHighlight(question, hasHighlight, mode, highlightContentItemId);
        }

        themeInstance.performPostComponentDidUpdate(`#question-${question.contentRevisionId}`);
    }

    scrollToScorableInput = () => {
        const { currentScorableItem } = this.props;
        const selectedScorableItem = document.querySelectorAll(`[placeholder="${currentScorableItem}"]`);
        const selectedInput = selectedScorableItem.item(0);
        if (selectedInput) {
            selectedInput.scrollIntoView({ block: 'center', inline: 'center' });
        }

        // scrollIntoView calls must be async to each other because question wrapper and keywords wrapper may or may not have the same scrollbar
        setTimeout(() => {
            const selectedKeyWord = document.getElementById(`keyword-${currentScorableItem}`);
            if (selectedKeyWord) {
                selectedKeyWord.scrollIntoView({ block: 'nearest', inline: 'center' });
            }
        }, 100);
    }

    componentWillUnmount() {
        const { isInSectionView, isRemoteOralExam, question: { contentRevisionId } } = this.props;
        // remote oral exam handles the question tracking by itself
        if (!isInSectionView || !isRemoteOralExam) {
            responseService.updateItemResultOnNavigation(contentRevisionId);
        }
    }

    setAsVisited(contentRevisionId) {
        storeManager.getStore().dispatch(
            visitedQuestionsActions.setVisited(contentRevisionId)
        );
    }

    activateQuestionAndHighlight = () => {
        const { question, activateAndScrollToQuestion, isCurrent, isInSectionView } = this.props;
        if (!isInSectionView || isCurrent || !activateAndScrollToQuestion) {
            return false;
        }

        return setTimeout(() => {
            activateAndScrollToQuestion(question.contentRevisionId, 10, false, true);
        }, 100);
    };

    focus() {
        const { isViewMediaScreen, shouldShowTitle } = this.props;
        if (!isViewMediaScreen) {
            return;
        }

        if (shouldShowTitle && this.h2) {
            this.h2.focus();
        } else if (this.questionNumberInstance) {
            this.questionNumberInstance.focus();
        }
    }

    initLoadTracker(props) {
        const { onReady, shouldShowPdfPanel, shouldShowScantronSketches, question } = props;

        this.loadTracker = new LoadTracker((success) => {
            if (typeof onReady === 'function') {
                onReady(success, this);
            }
        }, `QuestionDisplay - ${question.identifier}`);

        this.questionTracker = this.loadTracker.addItemToTrack('AssessmentItem');
        this.renderedTracker = this.loadTracker.addItemToTrack('QuestionDisplay');
        if (question.rubricBlockModel) {
            this.rubricBlockTracker = this.loadTracker.addItemToTrack('RubricBlock');
        }

        if (shouldShowPdfPanel) {
            this.pdfPanelTracker = this.loadTracker.addItemToTrack('PdfPanel');
        }

        if (shouldShowScantronSketches) {
            this.sketchesTracker = this.loadTracker.addItemToTrack('ScantronSketches');
        }

        this.loadTracker.setAllItemsAdded();
    }

    handleUserResponse = (...args) => {
        responseService.handleUserResponse(...args);
    };

    render() {
        const {
            additionalQuestionClassList,
            additionalWrapperClassList,
            annotatableQuestionHandler,
            choiceSequences,
            context,
            correctResponses,
            currentScorableItem,
            extendedRenderingActivated,
            flaggedItems,
            headerId,
            interactionSupportsRTL,
            isContentBlock,
            isCurrent,
            isDocument,
            isForm,
            isLastQuestionInCurrentNestedSection = false,
            isManualSelectorReadOnly,
            isReadOnly,
            isReadOnlyBasedOnManualSelectionSetup,
            isSplitQuestionView,
            isWordFormationQuestion,
            mainQuestionWrapperLabel,
            mode,
            outcomeVariables,
            presignedUrlsForPDFPanel,
            question,
            questionInteractionVisibility,
            questionNumberId,
            section,
            sectionHasStimulus,
            sectionQuestionsCount,
            settingValues,
            shouldShowInteractionFeedback,
            shouldShowManualSelector,
            shouldShowModalFeedback,
            shouldShowPdfPanel,
            shouldShowScantronSketches,
            shouldShowTitle,
            showFlagging,
            showKeyWordListBlock,
            showQuestionRubricBlock,
            stimulusPosition,
            themeInstance,
            themeQuestionLabel,
            ui,
            useNumberPerInteraction,
            userResponses,
            viewMedia,
        } = this.props;

        const headerPrefix = (isDocument || isForm)
            ? null
            : <span className="sr-only" id={questionNumberId}>{t('generic.questionNumberPrefix')} {question.number}</span>;

        const wrapperClassList = [styles.questionDisplayWrapper, ...additionalWrapperClassList];
        const questionClassList = [styles.question, ...additionalQuestionClassList];
        const services = questionHelperServices.getServices();
        const mainQuestionWrapperClassList = [styles.mainQuestionWrapper];

        if (extendedRenderingActivated) {
            questionClassList.push(styles.extendedQuestionDisplay);
        }

        if (isWordFormationQuestion) {
            questionClassList.push(styles.extendedQuestionDisplayWordFormation);
            mainQuestionWrapperClassList.push(styles.extendedMainQuestionWrapperWF);
        }

        if (isSplitQuestionView) {
            wrapperClassList.push('split-question-view');
        }

        themeInstance.addQuestionWrapperClassNames(question, wrapperClassList);
        themeInstance.addQuestionClassNames(question.customFields, questionClassList);

        return (
            <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                className={wrapperClassList.join(' ')}
                onMouseDown={annotatableQuestionHandler}
                onTouchStart={annotatableQuestionHandler}
            >
                { question.rubricBlockModel &&
                    <QuestionRubricBlockDisplay
                        model={question.rubricBlockModel}
                        onReady={this.rubricBlockTracker}
                    />
                }

                { shouldShowPdfPanel &&
                    <QuestionPDFPanelDisplay
                        model={question.PDFPanelModel}
                        onReady={this.pdfPanelTracker}
                        presignedUrlsForPDFPanel={presignedUrlsForPDFPanel}
                        questionContentRevisionId={question.contentRevisionId}
                        stimulusPosition={stimulusPosition}
                    />
                }

                <div // eslint-disable-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
                    onClick={!useNumberPerInteraction ? this.activateQuestionAndHighlight : undefined}
                    className={questionClassList.join(' ')}
                    id={`question-${question.contentRevisionId}`}
                    role="main"
                    dir={ui.direction}
                >
                    { themeQuestionLabel }

                    <div className={styles.questionBody}>
                        { !useNumberPerInteraction &&
                            <QuestionNumber
                                isContentBlock={isContentBlock}
                                isDocument={isDocument}
                                isForm={isForm}
                                mode={mode}
                                noTitle={!shouldShowTitle}
                                question={question}
                                ref={(instance) => { this.questionNumberInstance = instance; }}
                                sectionHasStimulus={sectionHasStimulus}
                                sectionQuestionsCount={sectionQuestionsCount}
                            />
                        }

                        <div
                            className={mainQuestionWrapperClassList.join(' ')}
                            tabIndex="-1"
                            role="group"
                            aria-labelledby={mainQuestionWrapperLabel}
                        >
                            { shouldShowTitle &&
                                <h2 tabIndex="-1" ref={(h2) => { this.h2 = h2; }}>
                                    {headerPrefix} <span id={headerId}>{question.title}</span>
                                </h2>
                            }

                            { showQuestionRubricBlock &&
                                <QuestionRubricBlockWithTitle question={question} section={section} />
                            }

                            { !isReadOnly && shouldShowManualSelector &&
                                <QuestionManualSelection
                                    contentRevisionId={question.contentRevisionId}
                                    isReadOnly={isReadOnly || isManualSelectorReadOnly}
                                    key={`qps-${question.contentRevisionId}`}
                                />
                            }

                            <QTIAssessmentItem
                                callback={this.handleUserResponse}
                                choiceSequences={choiceSequences}
                                context={context}
                                correctResponses={correctResponses}
                                currentScorableItem={currentScorableItem}
                                direction={interactionSupportsRTL ? ui.direction : 'ltr'}
                                flaggedItems={flaggedItems}
                                isCurrent={isCurrent}
                                isReadOnly={isReadOnly || isReadOnlyBasedOnManualSelectionSetup}
                                key={question.contentRevisionId}
                                outcomeVariables={outcomeVariables}
                                question={question}
                                questionInteractionVisibility={questionInteractionVisibility}
                                reportReady={this.questionTracker}
                                services={services}
                                settingValues={settingValues}
                                userResponses={userResponses}
                                viewMedia={viewMedia}
                                feedback={this.props.feedback}
                            />

                            { !isSplitQuestionView &&
                                <QuestionDisplayFooter
                                    isLastQuestionInCurrentNestedSection={isLastQuestionInCurrentNestedSection}
                                    question={question}
                                />
                            }
                        </div>

                        <QuestionDisplayFlag
                            currentScorableItem={currentScorableItem}
                            noTitle={!shouldShowTitle}
                            question={question}
                            showFlagging={showFlagging}
                            viewMedia={viewMedia}
                        />
                    </div>

                    { shouldShowScantronSketches &&
                        <ScantronSketches
                            {...this.props}
                            onReady={this.sketchesTracker}
                        />
                    }

                    { shouldShowModalFeedback &&
                        <ModalFeedbackDisplay
                            {...this.props}
                            modalFeedback={this.props.feedback[constants.modalFeedbackKey]}
                        />
                    }

                    { shouldShowInteractionFeedback &&
                        <FeedbackDisplay
                            {...this.props}
                            choiceSequences={choiceSequences}
                            userResponses={userResponses}
                            services={services}
                        />
                    }
                </div>

                {showKeyWordListBlock &&
                    <QuestionKeyWordListDisplay {...this.props} question={question} />
                }

            </div>
        );
    }
}

export class QuestionDisplayPreparing extends React.PureComponent {
    static propTypes = {
        isReady: PropTypes.bool.isRequired,
        question: PropTypes.object,
    };

    componentDidMount() {
        const { isReady, question } = this.props;

        if (question && !isReady) {
            assessmentActions.prepareQuestion(question);
        }
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillReceiveProps(nextProps) {
        const nextQuestion = nextProps.question;

        if (nextQuestion && !nextProps.isReady) {
            assessmentActions.prepareQuestion(nextQuestion);
        }
    }

    render() {
        return this.props.isReady ? <Question {...this.props} /> : null;
    }
}

export class QuestionDisplayWrapper extends React.PureComponent {
    static propTypes = {
        isAllView: PropTypes.bool,
        isListeningComponent: PropTypes.bool,
        isPrintBased: PropTypes.bool,
        question: PropTypes.object,
        section: PropTypes.object,
        ui: PropTypes.object,
    };

    renderSectionRubric() {
        const { section, ui, isListeningComponent } = this.props;

        if (isListeningComponent) {
            return <SectionRubricBlock section={section} ui={ui} isPaperBased />;
        }
        return null;
    }


    render() {
        const { isAllView, isPrintBased, question } = this.props;
        return (
            <React.Fragment>
                { isPrintBased && isAllView && question.questionNumberingInSection === 1 &&
                    this.renderSectionRubric()
                }
                <QuestionDisplayPreparing {...this.props} />
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    let question;

    if (ownProps && ownProps.question) {
        question = ownProps.question;
    } else {
        const contentRevisionId = ownProps.contentRevisionId || state.currentlyShown.questionId;
        question = assessmentReader.getQuestion(contentRevisionId, state);
    }

    if (!question) {
        return {
            isReady: false,
        };
    }

    return questionDisplaySelector.computeProps(state, ownProps, question);
};

const QuestionDisplayContainer = connect(mapStateToProps)(QuestionDisplayWrapper);
export default QuestionDisplayContainer;
